import type {
  Benefit,
  ContentBlock,
  LandingPageContent,
  TableItem,
  TileItem,
} from './landingPageContent.ts';

/**
 * v1(`bodyContent`)과 v2(`sections[].blocks[]`)를 **하나의 렌더 모델**로 만든다.
 *
 * 컴포넌트는 이 모델만 알면 되므로, 페이지를 파일 단위로 하나씩 v2로 옮겨도
 * 렌더러를 갈아엎을 필요가 없다(플래그 데이 없음).
 *
 * v1 섹션의 블록 순서는 **기존 렌더 순서 그대로**다:
 * 문단 → 콜아웃 → 혜택 카드 → 표 → 타일.
 */

export interface RenderSection {
  /** DOM id이자 목차의 스크롤 타깃. v1은 항상 `section-{i}`. */
  anchorId: string;
  title: string;
  blocks: ContentBlock[];
}

export interface RenderModel {
  content: LandingPageContent;
  /** 제목 없는 옛 `string[]` 본문 — 섹션 없이 문단만 렌더한다. */
  looseParagraphs: string[];
  sections: RenderSection[];
  /** 목차 항목 → 스크롤할 DOM id. 범위 밖 항목은 이미 검증에서 걸러진다. */
  tableOfContentsTargets: string[];
}

function defaultAnchorId(index: number): string {
  return `section-${index}`;
}

function v1Blocks(section: {
  paragraphs: string[];
  callout?: { title: string; description: string };
  benefitCards?: Benefit[];
  table?: TableItem;
  tiles?: TileItem[];
  tilesAllowed?: boolean;
  tilesRestricted?: boolean;
}): ContentBlock[] {
  const blocks: ContentBlock[] = [];
  if (section.paragraphs.length > 0) {
    blocks.push({ type: 'paragraphs', items: section.paragraphs });
  }
  if (section.callout) {
    blocks.push({ type: 'callout', ...section.callout });
  }
  if (section.benefitCards && section.benefitCards.length > 0) {
    blocks.push({ type: 'benefitCards', items: section.benefitCards });
  }
  if (section.table) {
    blocks.push({ type: 'table', ...section.table });
  }
  if (section.tiles && section.tiles.length > 0) {
    blocks.push({
      type: 'tiles',
      items: section.tiles,
      ...(section.tilesAllowed != null ? { allowed: section.tilesAllowed } : {}),
      ...(section.tilesRestricted != null
        ? { restricted: section.tilesRestricted }
        : {}),
    });
  }
  return blocks;
}

export function normalizeLandingPageContent(
  content: LandingPageContent
): RenderModel {
  const looseParagraphs: string[] = [];
  const sections: RenderSection[] = [];

  if ('sections' in content) {
    content.sections.forEach((section, index) => {
      sections.push({
        anchorId: section.id ?? defaultAnchorId(index),
        title: section.title,
        blocks: section.blocks,
      });
    });
  } else {
    const body = content.bodyContent;
    if (body.length > 0 && typeof body[0] === 'string') {
      looseParagraphs.push(...(body as string[]));
    } else {
      (body as Exclude<typeof body, string[]>).forEach((section, index) => {
        sections.push({
          anchorId: defaultAnchorId(index),
          title: section.title,
          blocks: v1Blocks(section),
        });
      });
    }
  }

  const tableOfContentsTargets = (content.tableOfContents?.items ?? []).map(
    item => {
      if (item.sectionId != null) {
        const matched = sections.find(
          section => section.anchorId === item.sectionId
        );
        if (matched) return matched.anchorId;
      }
      return defaultAnchorId(item.sectionIndex ?? 0);
    }
  );

  return { content, looseParagraphs, sections, tableOfContentsTargets };
}

/** 페이지 전체(루트 + 섹션 인라인)에 혜택 카드가 하나라도 있는지 — 레이아웃 여백이 갈린다. */
export function hasInlineBenefitCards(model: RenderModel): boolean {
  return model.sections.some(section =>
    section.blocks.some(block => block.type === 'benefitCards')
  );
}
